import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Stack,
  ButtonGroup,
  Divider,
  Link
} from '@mui/material';
import { tokens } from "../../theme";
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import authService from '../../services/AuthService';
import splinterlandsService from '../../services/SplinterlandsService';
import keychainLogo from '../../assets/logo_keychain.svg';

// The donation recipient's Hive/Splinterlands username
const DONATION_RECIPIENT = "sps-validator";

const DonateModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasKeychain, setHasKeychain] = useState(false);

  // Donation states
  const [amount, setAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Subscribe to auth service to get login status and keychain availability
  useEffect(() => {
    const authUnsubscribe = authService.subscribe((data) => {
      setIsLoggedIn(data.isLoggedIn);
      setHasKeychain(data.hasKeychain);
    });
    
    return () => {
      authUnsubscribe();
    };
  }, []);
  
  // Load SPS balance when logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const balanceUnsubscribe = splinterlandsService.subscribe((data) => {
      setMaxAmount(data.balances.sps || 0);
    });
    
    return () => {
      balanceUnsubscribe();
    };
  }, [isLoggedIn]);
  
  // Preset amounts
  const presetAmounts = [10, 50, 100];
  
  // Select preset amount
  const handleSelectAmount = (value) => {
    setAmount(value);
    setCustomAmount("");
  };
  
  // Handle custom amount input
  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d*\.?\d*$/.test(value) && !isNaN(parseFloat(value)))) {
      setCustomAmount(value);
      if (value !== '') {
        setAmount(parseFloat(value));
      }
    }
  };
  
  // Open login modal
  const handleLoginClick = () => {
    onClose(); // Close the donate modal
    
    // Delay to prevent modal conflicts
    setTimeout(() => {
      // Dispatch an event to open the login modal from the parent
      const event = new CustomEvent('openLoginModal');
      document.dispatchEvent(event);
    }, 300);
  };
  
  // Get appropriate keychain link based on browser
  const getKeychainLink = () => {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isFirefox) {
      return 'https://addons.mozilla.org/en-US/firefox/addon/hive-keychain/';
    }
    return 'https://chrome.google.com/webstore/detail/hive-keychain/jcacnejopjdphbnjgfaaobbfafkihpep';
  };
  
  // Send donation
  const handleDonate = async () => {
    if (!amount || amount <= 0) {
      setError('Please enter an amount greater than 0');
      return;
    }
    
    if (amount > maxAmount) {
      setError(`You don't have enough SPS. Maximum amount is ${formatNumber(maxAmount)}`);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get user from AuthService
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !currentUser.username) {
        throw new Error('User not logged in');
      }
      
      // Use Hive Keychain to send SPS to the recipient
      const donationResult = await sendSpsTokens(currentUser.username, DONATION_RECIPIENT, amount);
      
      if (donationResult.success) {
        // Update balances
        await splinterlandsService.refreshBalances();
        
        // Call success callback
        if (onSuccess) {
          onSuccess({
            username: currentUser.username,
            amount: amount,
            recipient: DONATION_RECIPIENT
          });
        }
        
        // Close modal
        onClose();
      } else {
        setError(donationResult.message || 'An error occurred during the donation');
      }
    } catch (err) {
      console.error('Error sending SPS donation:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  // Send SPS tokens using Hive Keychain with the correct Splinterlands token transfer format
  const sendSpsTokens = (from, to, amount) => {
    return new Promise((resolve) => {
      if (!window.hive_keychain) {
        resolve({ success: false, message: 'Hive Keychain not found' });
        return;
      }
      
      // Format amount to 3 decimal places as per Hive token standard
      const formattedAmount = parseFloat(amount).toFixed(3);
      
      // Generate a random nonce string for the transaction
      const nonce = Math.random().toString(36).substring(2, 12);
      
      // Create the Splinterlands token transfer JSON format
      const json = JSON.stringify({
        token: "SPS",
        to: to,
        qty: parseFloat(formattedAmount),
        memo: "Support for BurnBabyBurn project",
        app: "burnbabyburnapp/1.0.0",
        n: nonce
      });
      
      // Call Hive Keychain to execute the Splinterlands token transfer
      window.hive_keychain.requestCustomJson(
        from,          // Username
        "sm_token_transfer", // The correct Splinterlands token transfer ID
        "Active",      // Required key type
        json,          // JSON data
        "Donate SPS to project", // Description for the user
        response => {
          console.log('Keychain response:', response);
          
          if (response.success) {
            resolve({ 
              success: true, 
              message: `${formattedAmount} SPS successfully sent to ${to}.`,
              txId: response.result
            });
          } else {
            resolve({ 
              success: false, 
              message: response.message || 'Transaction failed'
            });
          }
        }
      );
    });
  };
  
  // Number formatting
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { backgroundColor: colors.primary[400] }
      }}
    >
      <DialogTitle sx={{ color: colors.grey[100], display: 'flex', alignItems: 'center', gap: 1 }}>
        <VolunteerActivismIcon sx={{ color: colors.greenAccent[500] }} />
        Support This Project
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" color={colors.grey[100]}>
            <strong>We voluntarily forgo 95% of all donations</strong> by burning them to reduce the total SPS supply, directly benefiting the entire ecosystem. The remaining 5% is reinvested to sustain and grow the project.
          </Typography>
        </Alert>
        {!hasKeychain ? (
          // Show Keychain installation instructions if not installed
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                Hive Keychain extension is required to donate SPS.
              </Typography>
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 3 }} color={colors.grey[100]}>
              Hive Keychain is a browser extension that allows you to securely interact with Hive applications and the Splinterlands game.
            </Typography>
            
            <Button
              variant="contained"
              color="secondary"
              component={Link}
              href={getKeychainLink()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <img 
                src={keychainLogo} 
                alt="Hive Keychain" 
                style={{ height: '20px', marginRight: '8px' }} 
              />
              Install Hive Keychain
            </Button>
          </Box>
        ) : !isLoggedIn ? (
          // Show login prompt if keychain is installed but not logged in
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                You need to be logged in to donate SPS.
              </Typography>
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 3 }} color={colors.grey[100]}>
              Please log in with your Hive account to make a donation.
            </Typography>
            
            <Button
              variant="contained"
              onClick={handleLoginClick}
              color="secondary"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <img 
                src={keychainLogo} 
                alt="Hive Keychain" 
                style={{ height: '20px', marginRight: '8px' }} 
              />
              Login with Hive
            </Button>
          </Box>
        ) : (
          // Show donation form if logged in and keychain is installed
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" color={colors.grey[100]}>
              If you find this tool helpful and would like to see it continue to be developed and maintained, please consider supporting the project with an SPS donation.
            </Typography>
            
            <Box sx={{ my: 3, textAlign: 'center' }}>
              <Typography variant="subtitle1" color={colors.grey[100]} gutterBottom>
                Choose an amount to donate:
              </Typography>
              
              <ButtonGroup variant="outlined" sx={{ my: 2 }}>
                {presetAmounts.map((presetAmount) => (
                  <Button
                    key={presetAmount}
                    onClick={() => handleSelectAmount(presetAmount)}
                    variant={amount === presetAmount && customAmount === "" ? "contained" : "outlined"}
                    sx={{ 
                      borderColor: colors.greenAccent[500],
                      color: amount === presetAmount && customAmount === "" ? colors.grey[100] : colors.greenAccent[500],
                      bgcolor: amount === presetAmount && customAmount === "" ? colors.greenAccent[700] : 'transparent',
                      '&:hover': { 
                        bgcolor: amount === presetAmount && customAmount === "" ? colors.greenAccent[600] : colors.greenAccent[900]
                      }
                    }}
                  >
                    {presetAmount} SPS
                  </Button>
                ))}
              </ButtonGroup>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color={colors.grey[300]} gutterBottom>
                  Or enter a custom amount:
                </Typography>
                <TextField
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Custom amount"
                  inputProps={{
                    min: 0.001,
                    max: maxAmount,
                    step: 0.001,
                    style: { textAlign: 'center' }
                  }}
                  disabled={loading}
                  sx={{
                    maxWidth: '200px',
                    input: { color: colors.grey[100] },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: colors.grey[700] },
                      '&:hover fieldset': { borderColor: colors.grey[500] },
                      '&.Mui-focused fieldset': { borderColor: colors.greenAccent[500] },
                    }
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ 
              borderRadius: 1, 
              p: 2, 
              my: 2,
              border: `1px solid ${colors.primary[600]}` 
            }}>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>Recipient:</strong>
                  </Typography>
                  <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
                    {DONATION_RECIPIENT}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>Your SPS Balance:</strong>
                  </Typography>
                  <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="bold">
                    {formatNumber(maxAmount)} SPS
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>Donation Amount:</strong>
                  </Typography>
                  <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="bold">
                    {formatNumber(amount)} SPS
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: colors.grey[300] }}
        >
          Cancel
        </Button>
        
        {hasKeychain && isLoggedIn && (
          <Button 
            onClick={handleDonate}
            variant="contained"
            startIcon={<VolunteerActivismIcon />}
            disabled={loading || amount <= 0 || amount > maxAmount}
            sx={{ 
              bgcolor: colors.greenAccent[600],
              color: colors.grey[100],
              '&:hover': { bgcolor: colors.greenAccent[700] },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Donation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DonateModal;